// Sparks — zero-cost, ultra-shortlived burst posts.
//
// Sparks bypass the time wallet entirely and live for only a few minutes.
// Users get a fixed daily allowance so the mode encourages bursty, reactive
// posting without devolving into spam. They render differently in the feed
// (orange border, countdown, SPARK chip) and cannot be extended by votes.
//
// Mechanics:
//  - SPARK_LIFESPAN_SECONDS     : how long a spark lives before expiring
//  - SPARK_DAILY_CAP            : how many sparks a user can fire per day
//  - SPARK_WINDOW_SECONDS       : rolling window used for cap accounting
//
// The server-side RPC `create_spark(p_value TEXT)` is the source of truth
// for both the cap and the lifespan. The constants below must stay in sync
// with that RPC (kept in the `010_sparks.sql` migration).

export const SPARK_LIFESPAN_SECONDS = 5 * 60;           // 5 minutes
export const SPARK_DAILY_CAP = 15;
export const SPARK_WINDOW_SECONDS = 24 * 60 * 60;       // 24h rolling

/**
 * Format the remaining lifespan of a spark as a compact MM:SS countdown.
 * Returns "00:00" once expired.
 */
export function formatSparkRemaining(expireAtISO: string, now = new Date()): string {
  const expireMs = new Date(expireAtISO).getTime();
  const diff = Math.max(0, Math.floor((expireMs - now.getTime()) / 1000));
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Compute what fraction (0..1) of a spark's life has elapsed.
 * Used to drive the shrinking progress bar on each card.
 */
export function sparkProgress(createdAtISO: string, expireAtISO: string, now = new Date()): number {
  const start = new Date(createdAtISO).getTime();
  const end = new Date(expireAtISO).getTime();
  const total = end - start;
  if (total <= 0) return 1;
  const elapsed = now.getTime() - start;
  return Math.max(0, Math.min(1, elapsed / total));
}
